import React from 'react';
import { useTranslation } from 'react-i18next';
import * as S from './SortSelector.styles';
import { BottomSheet } from '@/components/UI/BottomSheet/BottomSheet';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdOrder } from '@/api/types/ads.types';
import { SortIcon } from '@/components/UI/Icons/Icons';
import { useResponsive } from '@/hooks/useResponsive';
import { SortDropdown } from '@/components/Filters/SortDropdown';
import { Checkbox } from '@/components/UI/Checkbox/Checkbox';

interface Props {
  value?: AdOrder;
  onChange: (value: AdOrder) => void;
}

export const SortSelector = ({ value = AdOrder.DateDesc, onChange }: Props) => {
  const [isOpen, toggle] = useToggleOpenState();
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();

  const options = Object.values(AdOrder).filter(el => el !== AdOrder.Random);

  function handleChangeOrder(newValue: AdOrder) {
    onChange(newValue);
    toggle(false);
  }

  if (isDesktop) {
    return (
      <>
        <S.DesktopSortButton $isActive={isOpen} onClick={toggle}>
          <SortIcon width={14.4} height={12} />
          {t(`common.${value.toLowerCase()}`)}
          <S.DropdownIcon $isActive={isOpen} />
          <SortDropdown isOpen={isOpen} onClose={() => toggle(false)}>
            <S.Menu>
              {options.map(item => (
                <S.MenuItem
                  key={item}
                  id={
                    item !== AdOrder.DateDesc
                      ? `order_${item.toLowerCase()}`
                      : undefined
                  }
                  $isActive={item === value}
                  data-testid="sort-item"
                  onClick={() => handleChangeOrder(item)}
                >
                  <S.MenuItemText>
                    {t(`common.${item.toLowerCase()}`)}
                  </S.MenuItemText>
                  <S.RadioButton $isActive={item === value} />
                </S.MenuItem>
              ))}
            </S.Menu>
          </SortDropdown>
        </S.DesktopSortButton>
      </>
    );
  }

  return (
    <>
      <S.SortButton appearance="transparent" onClick={toggle}>
        <SortIcon width={16} height={16} />
        {t(`common.${value.toLowerCase()}`)}
      </S.SortButton>
      <BottomSheet
        isInPortal
        hasHeadBorder={false}
        isOpen={isOpen}
        close={() => toggle(false)}
        title={t('common.sorting')}
      >
        <S.BottomSheetContent>
          {options.map(item => (
            <Checkbox
              key={item}
              id={
                item !== AdOrder.DateDesc
                  ? `order_${item.toLowerCase()}`
                  : undefined
              }
              onChangeHook={() => handleChangeOrder(item)}
              value={item === value}
              label={t(`common.${item.toLowerCase()}`)}
              appearance="radio"
              size="lg"
              data-testid="sort-item"
            />
          ))}
        </S.BottomSheetContent>
      </BottomSheet>
    </>
  );
};
